import { cn } from '@/lib/utils';

interface ProgressProps {
  value: number;
  className?: string;
  tone?: 'primary' | 'success' | 'accent';
  size?: 'sm' | 'md';
  label?: string;
}

const TONES = {
  primary: 'bg-primary',
  success: 'bg-success',
  accent: 'bg-accent',
} as const;

export function Progress({ value, className, tone = 'primary', size = 'md', label }: ProgressProps) {
  const clamped = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn('w-full overflow-hidden rounded-full bg-surface-muted', size === 'sm' ? 'h-1.5' : 'h-2.5', className)}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-500 ease-out', TONES[tone])}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

interface RingProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  children?: React.ReactNode;
}

/** Anneau de progression utilise sur le tableau de bord apprenant. */
export function ProgressRing({ value, size = 128, strokeWidth = 10, className, children }: RingProps) {
  const clamped = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          className="fill-none stroke-surface-muted"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="fill-none stroke-primary transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      {/* Le libelle peut etre plus large que l'anneau : sans alignement centre,
          ses lignes se calent a gauche et l'ensemble parait decale. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center leading-tight">
        {children}
      </div>
    </div>
  );
}
